import { ChevronDown } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Avatar, AiChip } from "./avatar";
import type { MissionBrief, Participant } from "../types";

const BRIEF_TEXT_CLASS =
  "pt-1.5 pb-1 font-serif text-base leading-normal whitespace-pre-wrap text-parchment italic wrap-anywhere";

const ruleLabel = (participant: Participant) =>
  participant.handoffState === "HUMAN"
    ? "Staff member has taken over"
    : participant.handoffState === "REQUESTED"
      ? "AI assistant · Paused, person requested"
      : participant.aiRule === "ALL_MESSAGES"
        ? "AI assistant · Every human message"
        : participant.aiRule === "ADDRESSED_ONLY"
          ? "AI assistant · When mentioned or replied to"
          : "AI assistant · No automatic replies";

const ruleHint = (participant: Participant) =>
  participant.handoffState === "HUMAN"
    ? "A staff member has taken over this conversation."
    : participant.handoffState === "REQUESTED"
      ? "A person has been requested. The assistant is paused until someone takes over."
      : participant.aiRule === "ALL_MESSAGES"
        ? "all: replies to every human message."
        : participant.aiRule === "ADDRESSED_ONLY"
          ? "@ only: replies only when mentioned or replied to."
          : "Does not reply automatically.";

export function ConversationDetails({
  participants,
  memberId,
  rosterOpen,
  briefs,
}: {
  participants: Participant[];
  memberId: string;
  rosterOpen: boolean;
  briefs: MissionBrief[];
}) {
  return (
    <>
      {rosterOpen && (
        <ul
          className="max-h-[190px] shrink-0 overflow-y-auto border-b border-border bg-card/95 px-4 py-2"
          aria-label="Conversation members"
        >
          {participants.map((participant) => (
            <li
              key={participant.memberId}
              className="flex items-center gap-2 py-1 text-sm"
            >
              <Avatar
                name={participant.name}
                ai={participant.role === "ASSISTANT"}
                size="sm"
              />
              <strong className="flex-1 font-normal">
                {participant.name}
                {participant.memberId === memberId ? " (you)" : ""}
              </strong>
              <span className="text-right">
                {participant.role === "ASSISTANT" && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        tabIndex={0}
                        className="inline-flex rounded-xs outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                      >
                        <AiChip rule={participant.aiRule} />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      side="left"
                      className="z-[2147483001] font-sans"
                    >
                      {ruleHint(participant)}
                    </TooltipContent>
                  </Tooltip>
                )}
                <span className="block font-mono text-[11px] text-muted-foreground">
                  {participant.role === "ASSISTANT"
                    ? ruleLabel(participant)
                    : participant.role === "OWNER"
                      ? "Group owner"
                      : participant.role === "STAFF"
                        ? "Staff"
                        : "Member"}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {briefs.length > 0 && (
        <div className="max-h-[180px] shrink-0 overflow-y-auto">
          {briefs.map((brief) => (
            <details
              className="group border-b border-gold/35 border-l-2 border-l-gold bg-gold/10 px-3"
              key={`${brief.assistantId}:${brief.stepId}`}
            >
              <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 text-sm text-gold outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold/60 [&::-webkit-details-marker]:hidden">
                <span className="shrink-0 font-semibold">Your mission</span>
                {brief.title && (
                  <span className="min-w-0 flex-1 truncate font-medium text-parchment">
                    <span aria-hidden="true" className="mr-2 text-gold/50">
                      ·
                    </span>
                    {brief.title}
                  </span>
                )}
                <ChevronDown
                  aria-hidden="true"
                  className="ml-auto size-5 shrink-0 text-gold transition-transform group-open:rotate-180 motion-reduce:transition-none"
                />
              </summary>
              <div className="pb-3">
                {brief.brief ? (
                  <p className={BRIEF_TEXT_CLASS}>{brief.brief}</p>
                ) : (
                  <p className={BRIEF_TEXT_CLASS}>
                    {brief.title || brief.stepName}
                  </p>
                )}
                {brief.helpText && (
                  <p className="mt-2 text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
                    {brief.helpText}
                  </p>
                )}
                {brief.goal && (
                  <p className={BRIEF_TEXT_CLASS}>
                    <strong>Goal: </strong>
                    {brief.goal}
                  </p>
                )}
              </div>
            </details>
          ))}
        </div>
      )}
    </>
  );
}
